<template>
    <div>
        <template v-if="pageLoading">
            <PageSpinner />
        </template>
    
        <EmptyPage v-else-if="err" state="error">
            <template #button>
                <BaseButton
                    label="Reload" 
                    icon-name="refresh" 
                    icon-width="16px"
                    @submit="handleFetchMeetings"
                />
            </template> 
        </EmptyPage>

        <template v-else>
            <div class="zm__details" :class="{ 'disabled': deleteLoading}">
                <div class="zm__details__header">
                    <p class="zm__details__title">
                        {{ currentMeetingItem.title }}
                    </p>
                    <div class="row__right pr--0 pl--0">
                        <div class="dropdown mr--5">
                            <BaseButton 
                                id="meetingDetailsDropDown" 
                                data-bs-toggle="dropdown" 
                                aria-expanded="false"
                                prefix-icon
                                icon-name="dots-horizontal-rounded"
                                variant="outline" 
                                variant-style="secondary"
                            />

                            <List id="meetingDetails" :class="{ 'disabled': itemLoading }">
                                <ListItem flex pointer @click="editMeeting">
                                    <IconSvg name="edit" />
                                    <span class="ml--10 text--sm text-gray-800">Edit</span>
                                </ListItem>
                                <ListItem flex pointer @click="duplicateMeeting">
                                    <IconSvg name="duplicate" />
                                    <span class="ml--10 text--sm text-gray-800">Duplicate</span>
                                </ListItem>
                                <li><hr class="dropdown-divider dropdown--divider"></li>
                                <ListItem flex pointer @click="$emit('open-delete', currentMeetingItem)">
                                    <IconSvg class="mr--5" name="delete" fill="#e04060" /> 
                                    <span class="text-error-400 text--sm">Delete</span>
                                </ListItem>
                            </List>
                        </div>
                    </div>
                </div>
        
                <div>
                    <div class="flex align-items-center">
                        <IconSvg name="time" width="16px" class="mr--3" />
                        <FormLabel title="Appointment time" label-type="menu" class="mb--0" />
                    </div>
                    <p class="text--sm text-gray-600 mt--5">
                        {{ formatDateRange(currentMeetingItem.startDate, currentMeetingItem.endDate) }}
                    </p>
                </div>
        
                <div class="grid gap-3">
                    <div>
                        <div class="flex align-items-center">
                            <IconSvg name="report" width="16px" class="mr--3" />
                            <FormLabel title="Description" label-type="menu" no-margin-bottom />
                        </div>
                        <p class="mt--5 text--sm text-gray-600">
                            {{ currentMeetingItem.description || 'N/A' }}
                        </p>
                    </div>
        
                    <div>
                        <div class="flex align-items-center mb--5">
                            <FormLabel title="Linked clients" label-type="title" no-margin-bottom />
                        </div>
        
                        <div class="grid gap-2">
                            <div 
                                v-for="item in currentMeetingItem.attendees" :key="item" 
                                class="flex align-items-center gap-3"
                            >
                                <div class="zm__attendees__icon" :class="[item.responseStatus]">
                                    <IconSvg v-if="item.responseStatus === 'declined'" name="no-entry" />
                                    <IconSvg v-if="item.responseStatus === 'confirmed'" name="check" />
                                    <IconSvg v-if="item.responseStatus === 'unconfirmed' || item.responseStatus === 'needsAction'" name="user" />
                                </div>  
                                  
                                <div>
                                    <p class="text--sm text-gray-900 text--medium">
                                        {{ item.displayName || 'No name given' }}
                                    </p>

                                    <p class="text--sm text-gray-400">
                                        {{ item.email }}
                                    </p>
                                </div>                        
                            </div>
                        </div>
                    </div>
        
                    <div>
                        <div><hr class="dropdown-divider dropdown--divider"></div>

                        <div class="flex align-items-center justify-content-between">
                            <div class="flex align-items-center">
                                <FormLabel title="Meeting notes" label-type="title" />
                            </div>
        
                            <BaseButton 
                                variant="ghost" 
                                variant-style="secondary" 
                                label="+ Add note" 
                                bold
                                class="p--0"
                                @submit="openNoteModal({ action: 'add' })"
                            />
                        </div>
                        
                        <div class="mt--15">
                            <template v-if="currentMeetingItem.notes && currentMeetingItem.notes.length">
                                <div 
                                    v-for="item in currentMeetingItem.notes" 
                                    :key="item._id" 
                                    class="meta__note mb--5 flex align-items-center justify-content-between"
                                >
                                    <p class="text--sm text-gray-700">
                                        {{ item.value }}
                                    </p>
                                    <div class="dropdown">
                                        <BaseButton 
                                            id="noteDropdown" 
                                            data-bs-auto-close="true" 
                                            aria-expanded="false" 
                                            data-bs-toggle="dropdown" 
                                            variant="ghost"
                                            prefix-icon 
                                            icon-name="dots-horizontal-rounded" 
                                            icon-fill="#637579" 
                                            icon-width="16px"
                                        />

                                        <List id="noteDetails" :class="{ 'disabled': currentNote._id === item._id && noteAction === 'delete' }">
                                            <ListItem flex pointer @click="openNoteModal({ action: 'edit', item })">
                                                <IconSvg class="mr--5" name="edit" fill="#64748b" /> 
                                                <span class="text--sm">Edit note</span>
                                            </ListItem>
                                            <ListItem flex pointer @click="$emit('delete-note', item)">
                                                <IconSvg class="mr--5" name="delete" fill="#e04060" /> 
                                                <span class="text-error-400 text--sm">Delete note</span>
                                            </ListItem>
                                        </List>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <p class="project__item__desc">
                                    No notes yet.
                                </p>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { 
    IconSvg, 
    BaseButton, 
    FormLabel,
    EmptyPage,
    PageSpinner,
    List,
    ListItem,
} from "@/components/ui/index";
import { formatDateRange } from "@/utils/date";

export default {
    name: "SingleMeeting",

    components: {
        IconSvg,
        BaseButton, 
        FormLabel,
        EmptyPage,
        PageSpinner,
        List,
        ListItem,
    },

    props: {
        currentMeetingItem: { type: Object, default: () => ({}) },
        currentNote: { type: Object, default: () => ({}) },
        pageLoading: { type: Boolean, default: false },
        itemLoading: { type: Boolean, default: false },
        noteAction: { type: String, default: "" },
        err: { type: Boolean, default: false },
    },

    emits: ["open-note", "open-delete", "open-edit", "duplicate-complete", "delete-note"],

    data() {
        return {
            deleteLoading: false,
        };
    },

    computed: {
        userProfile() {
            return this.$store.getters["auth/profile"];
        },
    },

    methods: {
        formatDateRange
        ,
        openNoteModal({ action, item = undefined }) {
            this.$emit("open-note", { action, item });
        },

        buildPayload() {
            return {
                eventPayload: {
                    summary: this.currentMeetingItem.title,
                    location: this.currentMeetingItem.location,
                    description: this.currentMeetingItem.description,
                    attendees: this.currentMeetingItem.attendees.map(item => ({ email: item.email, displayName: item.displayName })),
                    start: {
                        dateTime: this.currentMeetingItem.startDate,
                        timeZone: this.currentMeetingItem.timeZone,
                    },
                    end: {
                        dateTime: this.currentMeetingItem.endDate,
                        timeZone: this.currentMeetingItem.timeZone,
                    },
                },
                meetingPayload: {
                    recurringEventType: this.currentMeetingItem.recurrenceType,
                    providerKey: this.currentMeetingItem.providerKey,
                    timeZone: this.currentMeetingItem.timeZone,
                },
            };
        },

        editMeeting() {
            this.$emit("open-edit", this.currentMeetingItem);
        },

        async duplicateMeeting() {
            if (!this.currentMeetingItem._id) return;
            
            try {
                this.deleteLoading = true;

                const payload = this.buildPayload();
                const resp = await this.$store.dispatch("meeting/duplicateMeeting", payload);

                this.$toast.success("Meeting duplicated");
                this.$emit("duplicate-complete", resp);    
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            }
        },
    },
};
</script>